import { Dispatch, Fragment, SetStateAction } from 'react';
import {
  Box,
  Button,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Form, Formik } from 'formik';
import AddIcon from '@mui/icons-material/Add';
import TuneIcon from '@mui/icons-material/TuneRounded';
import { toast } from 'react-toastify';
import FullScreenFloatingModal from '../../modal/FullScreenFloatingModal';
import SegmentFilter from './SegmentFilter';
import {
  CampaignFilter,
  CampaignSegment,
  campaignSegmentSchema,
} from '../../../types/marketing';
import { DEFAULT_CAMPAIGN_FILTER } from '../../../constants/marketing';
import { FormikForm } from '../../../interfaces';
import { validateFilters } from '../../../helpers/marketing';
import ConditionButton from './ConditionButton';
import useGetMarketingData from '../../../hooks/marketing/useGetMarketingData';

interface Props {
  segment: CampaignSegment | null;
  isOpen: boolean;
  onClose: () => void;
  onReview: (segment: CampaignSegment) => void;
}

type SetValues = Dispatch<SetStateAction<CampaignSegment>>;

function CampaignSegmentCreator({ segment, isOpen, onClose, onReview }: Props) {
  const theme = useTheme();
  const isMediumOrLarger = useMediaQuery<Theme>(theme.breakpoints.up('md'));
  const { data: marketingData } = useGetMarketingData();

  const form: FormikForm<CampaignSegment> = {
    initialValues: {
      id: segment?.id ?? undefined,
      topPercentage: segment?.topPercentage ?? undefined,
      condition: segment?.condition ?? 'ALL',
      filters: segment?.filters ?? [DEFAULT_CAMPAIGN_FILTER],
    },
    validationSchema: campaignSegmentSchema,
    onSubmit: async values => {
      if (!marketingData) return;
      await campaignSegmentSchema.validate(values);
      try {
        const validatedFilters = validateFilters(
          values.filters as unknown as CampaignFilter[],
          marketingData,
        );
        const newSegment = {
          ...values,
          filters: validatedFilters,
        };
        onReview(newSegment);
        onClose();
      } catch (error) {
        toast.error('One or more filters are invalid.');
        throw error;
      }
    },
  };

  const onDeleteFilter = (setValues: SetValues, index: number) => {
    setValues(prev => {
      const newFilters = [...prev.filters];
      newFilters.splice(index, 1);
      return {
        ...prev,
        filters: newFilters,
      };
    });
  };

  const onAddFilter = (setValues: SetValues) => {
    setValues(prev => ({
      ...prev,
      filters: [...prev.filters, DEFAULT_CAMPAIGN_FILTER],
    }));
  };

  const onUpdateFilter = (
    setValues: SetValues,
    index: number,
    filter: Partial<CampaignFilter>,
  ) => {
    setValues(prev => {
      const newFilters = [...prev.filters];
      newFilters[index] = {
        ...newFilters[index],
        ...filter,
      };
      return {
        ...prev,
        filters: newFilters,
      };
    });
  };

  const modalName = isMediumOrLarger
    ? 'Build Your Own Segment'
    : 'Build A Segment';

  return (
    <Formik
      enableReinitialize
      initialValues={form.initialValues}
      validationSchema={form.validationSchema}
      onSubmit={form.onSubmit}>
      {helpers => {
        const {
          isSubmitting,
          isValid,
          isValidating,
          dirty,
          values,
          setValues,
        } = helpers;
        const isDisabled =
          isSubmitting || !isValid || isValidating || (!dirty && !segment);

        return (
          <Form
            style={{
              width: '100%',
              padding: 0,
              boxSizing: 'border-box',
            }}>
            <FullScreenFloatingModal
              isOpen={isOpen}
              name={modalName}
              onClose={onClose}
              submitButton={{
                text: 'Review',
                disabled: isDisabled,
                onClick: () => helpers.submitForm(),
                type: 'submit',
              }}>
              <Stack gap={3}>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color={theme.palette.medGrey.main}>
                  <TuneIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      color: 'inherit',
                    }}
                  />
                  <Typography variant="h3" color="inherit">
                    Segment Filters
                  </Typography>
                </Box>

                {values.filters?.map((filter, index: number) => (
                  <Fragment
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${(filter as CampaignFilter).category}-${index}`}>
                    {index > 0 && (
                      <ConditionButton
                        condition={values.condition}
                        onChange={value => {
                          if (!value) return;
                          setValues(prev => ({
                            ...prev,
                            condition: value,
                          }));
                        }}
                      />
                    )}
                    <SegmentFilter
                      filter={filter as CampaignFilter}
                      onChange={value =>
                        onUpdateFilter(setValues, index, value)
                      }
                      onDelete={
                        values.filters.length > 1
                          ? () => onDeleteFilter(setValues, index)
                          : undefined
                      }
                    />
                  </Fragment>
                ))}

                <Button
                  startIcon={
                    <AddIcon
                      sx={{
                        width: '14px',
                        height: '14px',
                      }}
                    />
                  }
                  variant="light"
                  sx={{
                    width: 'fit-content',
                  }}
                  onClick={() => onAddFilter(setValues)}>
                  <span>Add filter</span>
                </Button>
              </Stack>
            </FullScreenFloatingModal>
          </Form>
        );
      }}
    </Formik>
  );
}

export default CampaignSegmentCreator;
